import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
  faSort, faSortUp, faSortDown, faTimes, faFilter,
  faChevronLeft, faChevronRight, faAnglesLeft, faAnglesRight,
  faListOl
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// ============================================================
// CACHE DEXIE (24h)
// ============================================================
const db = new Dexie('CriteresCacheDB');
db.version(1).stores({ criteres: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCached(key, allowExpired = false) {
  try {
    const entry = await db.criteres.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}
async function setCached(key, data) {
  await db.criteres.put({ key, data, timestamp: Date.now() });
}
async function getStale(key) {
  return getCached(key, true);
}

// ============================================================
// HELPERS
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
  }
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null;
};

const API_URL = `${API_CONFIG2.apiUrl}criteria/api_criteres.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageCriteres = () => {
  // --- États ---
  const [criteres, setCriteres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('cache');

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState('asc'); // 'asc' ou 'desc'
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });

  // Modales
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCritere, setEditingCritere] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    libelle: '',
    description: '',
    ordre: 0
  });

  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // TOAST
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // RÉCUPÉRATION DES CRITÈRES
  // ============================================================
  const fetchCriteres = useCallback(async (page = 1, search = '', orderDir = 'asc', forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const params = new URLSearchParams();
    params.append('action', 'fetch');
    params.append('page', page);
    params.append('limit', pagination.limit);
    if (search) params.append('search', search);
    params.append('order', orderDir);
    const cacheKey = params.toString() || 'default';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    // ... (similaire aux autres composants, je vais l'implémenter mais pour simplifier on le fait directement)

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    try {
      if (!forceRefresh) {
        const cached = await getCached(cacheKey);
        if (cached) {
          if (isMounted.current) {
            setCriteres(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            setLoading(false);
            setRefreshing(false);
          }
          return;
        }
      }

      const url = `${API_URL}?${params.toString()}`;
      const response = await fetch(url, { signal: controller.signal });

      if (response.status === 429) {
        const stale = await getStale(cacheKey);
        if (stale) {
          if (isMounted.current) {
            setCriteres(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            showToast('429 – Cache utilisé', 'warning');
            setLoading(false);
            setRefreshing(false);
          }
          return;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        return fetchCriteres(page, search, orderDir, forceRefresh, retryCount + 1);
      }

      if (!response.ok) {
        const stale = await getStale(cacheKey);
        if (stale) {
          if (isMounted.current) {
            setCriteres(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            showToast('Erreur réseau – Cache utilisé', 'warning');
            setLoading(false);
            setRefreshing(false);
          }
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        const payload = { data: data.data, pagination: data.pagination };
        if (isMounted.current) {
          setCriteres(data.data);
          setPagination(data.pagination);
          const source = forceRefresh ? 'live (forcé)' : 'live';
          setDataSource(source);
          await setCached(cacheKey, payload);
          if (forceRefresh) showToast('Données actualisées', 'success');
          setLoading(false);
          setRefreshing(false);
        }
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      const stale = await getStale(cacheKey);
      if (stale && isMounted.current) {
        setCriteres(stale.data);
        setPagination(stale.pagination);
        setDataSource('cache (périmé)');
        showToast('Erreur – Cache utilisé', 'warning');
        setLoading(false);
        setRefreshing(false);
      } else {
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
      }
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchCriteres(1, val, order, false);
    }, 400);
  };

  // ============================================================
  // TRI
  // ============================================================
  const toggleOrder = () => {
    const newOrder = order === 'asc' ? 'desc' : 'asc';
    setOrder(newOrder);
    fetchCriteres(pagination.currentPage, searchTerm, newOrder, false);
  };

  // ============================================================
  // CRUD
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const action = editingCritere ? 'update' : 'create';
    const method = editingCritere ? 'PUT' : 'POST';
    const payload = { ...formData };

    try {
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        await db.criteres.clear();
        fetchCriteres(pagination.currentPage, searchTerm, order, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        await db.criteres.clear();
        fetchCriteres(pagination.currentPage, searchTerm, order, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur serveur', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // OUVERTURE MODALES
  // ============================================================
  const openEditModal = (item) => {
    setEditingCritere(item);
    setFormData({
      id: item.id,
      libelle: item.libelle,
      description: item.description || '',
      ordre: item.ordre || 0
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCritere(null);
    setFormData({ id: '', libelle: '', description: '', ordre: 0 });
    setShowModal(true);
  };

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!criteres.length) {
      showToast('Aucun critère à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Libellé', 'Description', 'Ordre'];
    const rows = criteres.map(c => [c.id, c.libelle, c.description, c.ordre]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `criteres_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // CLEAR CACHE
  // ============================================================
  const clearCache = async () => {
    await db.criteres.clear();
    showToast('Cache vidé', 'success');
    fetchCriteres(pagination.currentPage, searchTerm, order, true);
  };

  // ============================================================
  // INITIALISATION
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchCriteres(1, '', 'asc', false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // ============================================================
  // RENDU
  // ============================================================
  if (loading && !criteres.length && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des critères...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* Styles identiques à ManageFactures pour l'uniformité */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-glow: rgba(59, 130, 246, 0.25);
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .monetra-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          font-weight: 500;
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.icon-only {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.2rem;
          margin-top: 0.5rem;
        }

        .critere-card {
          background: rgba(255,255,255,0.03);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: var(--radius);
          padding: 1.2rem;
          transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }
        .critere-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-blue), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }
        .critere-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: rgba(59, 130, 246, 0.2);
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.3);
        }
        .critere-card:hover::before {
          opacity: 1;
        }

        .critere-card .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        .critere-card .card-id {
          font-size: 0.7rem;
          color: var(--text-muted);
          background: rgba(255,255,255,0.04);
          padding: 2px 10px;
          border-radius: 12px;
          font-weight: 600;
        }
        .critere-card .card-ordre {
          font-size: 0.7rem;
          color: var(--text-secondary);
          background: rgba(255,255,255,0.04);
          padding: 2px 10px;
          border-radius: 12px;
        }
        .critere-card .libelle {
          font-weight: 600;
          color: #fff;
          font-size: 1.05rem;
          margin-bottom: 4px;
        }
        .critere-card .description {
          color: var(--text-secondary);
          font-size: 0.9rem;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .critere-card .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
          margin-top: auto;
          padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.04);
        }

        .toast-container {
          position: fixed;
          top: clamp(12px, 2vh, 24px);
          right: clamp(12px, 2vw, 24px);
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 12px 18px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .btn-close-custom {
          background: transparent; border: none; color: var(--text-muted);
          cursor: pointer; font-size: 1.2rem; padding: 0 4px; margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input::placeholder {
          color: var(--text-muted);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .gap-2 { gap: 8px; }
        .mb-3 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-center { text-align: center; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.8rem); }
        .display-6 { font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: 700; }
        .m-0 { margin: 0; }
        .p-3 { padding: 1rem; }
        .p-0 { padding: 0; }
        .overflow-hidden { overflow: hidden; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }

        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 1.5rem;
          padding-top: 1.2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-buttons {
          display: flex;
          gap: 4px;
          align-items: center;
          flex-wrap: wrap;
        }
        .pagination-buttons button {
          padding: 6px 12px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.2s;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          min-height: 36px;
          cursor: pointer;
          min-width: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pagination-buttons button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .pagination-buttons button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-buttons button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .pagination-buttons .page-ellipsis {
          padding: 0 6px;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .monetra-page { padding: 12px; }
          .glass-card { padding: 1rem; }
          .flex-between { flex-direction: column; align-items: stretch; }
          .cards-grid { grid-template-columns: 1fr; }
          .card-actions { flex-wrap: wrap; }
          .card-actions .monetra-btn { flex: 1; }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.65rem; padding: 4px 10px; min-height: 34px; }
          .critere-card { padding: 0.8rem; }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="flex-between mb-3">
          <div>
            <h1 className="display-6 fw-bold mb-1">📋 Critères d'évaluation</h1>
            <p className="text-secondary m-0">Gérez les critères utilisés dans les évaluations</p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn" onClick={() => fetchCriteres(pagination.currentPage, searchTerm, order, false)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={() => fetchCriteres(pagination.currentPage, searchTerm, order, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={clearCache} disabled={loading}>
              <FontAwesomeIcon icon={faTrash} /> Vider cache
            </button>
            <button className="monetra-btn" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Recherche et tri */}
        <div className="glass-card mb-3">
          <div className="flex-between">
            <div className="search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
              <input
                type="text"
                placeholder="Rechercher (libellé, description...)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="d-flex gap-2 align-items-center">
              <button className="monetra-btn" onClick={toggleOrder}>
                <FontAwesomeIcon icon={order === 'asc' ? faSortUp : faSortDown} /> Ordre {order === 'asc' ? 'croissant' : 'décroissant'}
              </button>
              <span className="text-secondary small">
                <strong>{pagination.totalRecords}</strong> critères
              </span>
            </div>
          </div>
        </div>

        {/* Grille de cartes */}
        <div className="glass-card p-0 overflow-hidden">
          {loading && !refreshing ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : criteres.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucun critère trouvé.</div>
          ) : (
            <div className="cards-grid" style={{ padding: '1rem' }}>
              {criteres.map(item => (
                <div key={item.id} className="critere-card">
                  <div className="card-header">
                    <span className="card-id">#{item.id}</span>
                    <span className="card-ordre">Ordre: {item.ordre}</span>
                  </div>
                  <div className="libelle">{item.libelle}</div>
                  {item.description && (
                    <div className="description">{item.description}</div>
                  )}
                  <div className="card-actions">
                    <button className="monetra-btn icon-only" onClick={() => openEditModal(item)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button className="monetra-btn icon-only danger" onClick={() => { setFormData(item); setShowDeleteModal(true); }}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="p-3 border-top flex-between">
            <div className="info">
              Page <strong>{pagination.currentPage}</strong> sur {pagination.totalPages} ({pagination.totalRecords} enregistrements)
            </div>
            <div className="pagination-buttons">
              <button
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchCriteres(1, searchTerm, order, false)}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </button>
              <button
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchCriteres(pagination.currentPage - 1, searchTerm, order, false)}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {[...Array(pagination.totalPages).keys()].map(num => {
                const p = num + 1;
                if (
                  p === 1 ||
                  p === pagination.totalPages ||
                  (p >= pagination.currentPage - 1 && p <= pagination.currentPage + 1)
                ) {
                  return (
                    <button
                      key={p}
                      className={p === pagination.currentPage ? 'active' : ''}
                      onClick={() => fetchCriteres(p, searchTerm, order, false)}
                    >
                      {p}
                    </button>
                  );
                }
                if (p === pagination.currentPage - 2 || p === pagination.currentPage + 2) {
                  return <span key={p} className="page-ellipsis">…</span>;
                }
                return null;
              })}
              <button
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchCriteres(pagination.currentPage + 1, searchTerm, order, false)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <button
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchCriteres(pagination.totalPages, searchTerm, order, false)}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale Ajout/Modification */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title><FontAwesomeIcon icon={faEdit} className="text-success me-2" /> {editingCritere ? 'Modifier' : 'Ajouter'} un critère</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <label className="text-secondary small">Libellé *</label>
                <input className="monetra-input" name="libelle" value={formData.libelle} onChange={(e) => setFormData({...formData, libelle: e.target.value})} required disabled={submitting} />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Description</label>
                <textarea className="monetra-input" name="description" rows="3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} disabled={submitting} />
              </div>
              <div className="col-12">
                <label className="text-secondary small">Ordre</label>
                <input type="number" className="monetra-input" name="ordre" value={formData.ordre} onChange={(e) => setFormData({...formData, ordre: parseInt(e.target.value) || 0})} disabled={submitting} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} className="monetra-btn" disabled={submitting}>Annuler</Button>
            <Button variant="success" type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? <><FontAwesomeIcon icon={faSyncAlt} spin /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modale Suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger"><FontAwesomeIcon icon={faExclamationTriangle} /> Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le critère <strong>"{formData.libelle}"</strong> ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="monetra-btn" disabled={deleting}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? <><FontAwesomeIcon icon={faSyncAlt} spin /> Suppression...</> : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button className="btn-close-custom" onClick={() => setToast({ ...toast, show: false })}>✕</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageCriteres;